import type { ListWorksResponse, Work } from './useWorkApi'
import { useApiBase } from './useApiBase'

export interface CreateChildResponse {
  message: string
}

export interface ChildInfo {
  userId: string
  order: number
  name: string
  username: string
  exp: number
  /** 保護者が羽根筆を ON にしているか（#944） */
  quill: boolean
  /** 保護者が作品の公開を ON にしているか（#975） */
  crystallize: boolean
}

export interface ListChildrenResponse {
  message: string
  children: ChildInfo[]
}

/** Creator profile external links (#417). All fields optional. */
export interface ExternalLinks {
  x?: string | null
  github?: string | null
  itch?: string | null
  pixiv?: string | null
}

export interface UpdateUserAttributeRequest {
  name?: string
  /** Creator-page slug (#417). Send null/empty to unset and take the page down. */
  slug?: string | null
  /** Creator bio (Markdown, max 1000 chars). Send null/empty to clear. */
  bioMarkdown?: string | null
  /** External profile links. Send null to clear all. */
  externalLinks?: ExternalLinks | null
}

export interface UpdateUserAttributeResponse {
  message: string
  children: ChildInfo[]
}

/** Public creator profile (#417) — what `/c/<slug>` consumers see. */
export interface CreatorPublic {
  userId: string
  slug: string
  name: string
  /** 冒険者ランク (称号)。User.exp から get_name_of_adventurer_rank で算出。 */
  title: string
  bioMarkdown?: string | null
  externalLinks?: ExternalLinks | null
  /** ISO 8601. `User.created_at`、フロントで年月単位に丸めて表示。 */
  joinedAt: string
  works: Work[]
  totalWorks: number
}

export function useUserApi() {
  const { apiBase, authenticatedFetch, publicFetch } = useApiBase()

  const createChild = async (username: string) => {
    return authenticatedFetch<CreateChildResponse>(
      `${apiBase}/api/v1/children`,
      {
        method: 'POST',
        body: JSON.stringify({ username }),
      },
    )
  }

  const listChildren = async () => {
    const response = await authenticatedFetch<ListChildrenResponse>(
      `${apiBase}/api/v1/children`,
      { method: 'GET' },
    )
    return response.children
  }

  /** 保護者が子アカウントの設定を変える（#944 / #975）。変える項目を 1 つだけ送る。ON にするときは同意（consent）が要る */
  const updateChildSettings = async (
    childId: string,
    settings: { quill: boolean, consent?: boolean } | { crystallize: boolean, consent?: boolean },
  ) => {
    return authenticatedFetch<{ quill: boolean, crystallize: boolean }>(
      `${apiBase}/api/v1/children/${encodeURIComponent(childId)}/settings`,
      {
        method: 'PUT',
        body: JSON.stringify(settings),
      },
    )
  }

  const updateUserAttribute = async (request: UpdateUserAttributeRequest) => {
    await authenticatedFetch(
      `${apiBase}/api/v1/user/attr`,
      {
        method: 'PUT',
        body: JSON.stringify(request),
      },
    )
  }

  const getPublicCreator = async (slug: string) => {
    return publicFetch<CreatorPublic>(
      `${apiBase}/api/v1/public/creators/${slug}`,
      { method: 'GET' },
    )
  }

  /**
   * #803: `/c/<slug>` の追記スクロール用（2 ページ目以降）。
   *
   * 1 ページ目は `getPublicCreator` が SSR で同梱して返す —— `/c` は
   * `/works/<slug>` への数少ない公開クロール入口なので、初回ぶんは SSR HTML に
   * 載せておく必要がある。レスポンス形状は `listWorks` / `listMyWorks` と同じ。
   */
  const listCreatorWorks = async (slug: string, page?: number, perPage?: number) => {
    return publicFetch<ListWorksResponse>(
      `${apiBase}/api/v1/public/creators/${slug}/works`,
      {
        method: 'GET',
        query: { page, perPage },
      },
    )
  }

  return { createChild, listChildren, updateChildSettings, updateUserAttribute, getPublicCreator, listCreatorWorks }
}
